/* Import planning — handover §12.
 *
 * The dry run is the whole feature. An import that just writes is a tool you use
 * once, nervously; an import that tells you "12 create, 3 update, 1 skip" and
 * which fields change on each update is one you use every time a supplier sends
 * a spreadsheet.
 *
 * Two rules from §12 drive the design:
 *
 *   Match on the key field, so re-importing a corrected file updates rather than
 *   duplicates. Without this, fixing one typo and re-importing doubles the
 *   register — the failure that makes people stop trusting it.
 *
 *   Updates touch only mapped columns. An unmapped column is absent, not empty,
 *   so a narrow file cannot blank out fields it never mentioned.
 *
 * Pure — no services, no React. The screen renders what this returns.
 */

import type { CsvTable } from './csv';
import { ACCOUNT_KINDS } from './registerFields';

export type FieldKind = 'text' | 'date' | 'number' | 'choice' | 'depot';

export interface ImportField {
  /** Domain property name, e.g. 'tag'. */
  key: string;
  label: string;
  kind: FieldKind;
  /** Rows without a value here are skipped. */
  required?: boolean;
  /** The match key. Exactly one field per target sets this. */
  isKey?: boolean;
  /** Extra header spellings to auto-match against. */
  aliases?: string[];
  /** Allowed values for 'choice'. Matched case-insensitively. */
  options?: readonly string[];
}

export interface ImportTarget {
  id: 'devices' | 'people';
  label: string;
  fields: ImportField[];
}

export type RowAction = 'create' | 'update' | 'skip';

export interface FieldChange {
  field: string;
  label: string;
  from: unknown;
  to: unknown;
}

export interface PlannedRow {
  /** 1-based line in the file, counting the header, so it matches Excel. */
  line: number;
  action: RowAction;
  keyValue: string;
  /** Why it is being skipped, or why an update is a no-op. */
  reason?: string;
  /** Only mapped, valid fields. */
  values: Record<string, string | number>;
  changes: FieldChange[];
  existingId?: string;
  /** Per-cell problems that did not stop the row. */
  warnings: string[];
}

export interface ImportPlan {
  rows: PlannedRow[];
  counts: { create: number; update: number; skip: number; unchanged: number };
  /** Headers present in the file that map to nothing. Informational. */
  unmappedHeaders: string[];
  /** Required fields with no header mapped. Blocks the import. */
  missingRequired: ImportField[];
  /** True when the plan is safe to apply. */
  ready: boolean;
}

/* ─────────────────────────── Targets ─────────────────────────── */

const DEPOT_OPTIONS = ['Hull', 'Doncaster', 'London'] as const;

export const IMPORT_TARGETS: ImportTarget[] = [
  {
    id: 'devices',
    label: 'Devices',
    fields: [
      { key: 'tag', label: 'Asset tag', kind: 'text', required: true, isKey: true, aliases: ['tag', 'asset', 'assetid', 'assetnumber'] },
      { key: 'type', label: 'Type', kind: 'choice', options: ['Laptop', 'Desktop', 'Mobile', 'Tablet', 'Server', 'Firewall', 'Switch', 'Access point', 'Printer', 'Storage', 'UPS'], aliases: ['devicetype', 'category'] },
      { key: 'make', label: 'Make', kind: 'text', aliases: ['manufacturer', 'vendor', 'brand'] },
      { key: 'model', label: 'Model', kind: 'text' },
      { key: 'serial', label: 'Serial', kind: 'text', aliases: ['serialnumber', 'servicetag', 'sn'] },
      { key: 'cpu', label: 'CPU', kind: 'text', aliases: ['processor'] },
      { key: 'ram', label: 'RAM', kind: 'text', aliases: ['memory'] },
      { key: 'disk', label: 'Storage', kind: 'text', aliases: ['disk', 'harddisk', 'ssd'] },
      { key: 'os', label: 'Operating system', kind: 'text', aliases: ['os', 'operatingsystem'] },
      { key: 'bought', label: 'Purchase date', kind: 'date', aliases: ['purchased', 'purchasedate', 'bought', 'acquired'] },
      { key: 'warranty', label: 'Warranty ends', kind: 'date', aliases: ['warranty', 'warrantyend', 'warrantyexpiry', 'supportends'] },
      { key: 'loc', label: 'Depot', kind: 'depot', options: DEPOT_OPTIONS, aliases: ['depot', 'site', 'location', 'office'] },
      { key: 'ip', label: 'IP address', kind: 'text', aliases: ['ip', 'ipaddress'] },
      { key: 'status', label: 'Status', kind: 'text' },
      { key: 'notes', label: 'Notes', kind: 'text', aliases: ['comment', 'comments', 'remarks'] },
    ],
  },
  {
    id: 'people',
    label: 'People',
    fields: [
      { key: 'upn', label: 'Email / UPN', kind: 'text', required: true, isKey: true, aliases: ['upn', 'email', 'emailaddress', 'username', 'userprincipalname'] },
      { key: 'name', label: 'Display name', kind: 'text', required: true, aliases: ['name', 'fullname', 'displayname'] },
      { key: 'title', label: 'Job title', kind: 'text', aliases: ['jobtitle', 'role', 'position'] },
      { key: 'dept', label: 'Department', kind: 'text', aliases: ['dept', 'department', 'team'] },
      /* Importable because the alternative is clicking through a hundred records.
         Graph cannot tell a shared mailbox from a person, so somebody has to
         say — and a spreadsheet is a far better place to say it a hundred times
         than a form. Blank stays blank: an unclassified account is a real state
         the register shows, not a missing value to guess at. */
      { key: 'kind', label: 'Account type', kind: 'choice', options: ACCOUNT_KINDS, aliases: ['kind', 'accounttype', 'type', 'accountkind'] },
      { key: 'loc', label: 'Depot', kind: 'depot', options: DEPOT_OPTIONS, aliases: ['depot', 'site', 'location', 'office'] },
      { key: 'mobile', label: 'Mobile', kind: 'text', aliases: ['mobile', 'phone', 'mobilenumber'] },
      { key: 'start', label: 'Start date', kind: 'date', aliases: ['start', 'startdate', 'joined'] },
      { key: 'mgr', label: 'Manager', kind: 'text', aliases: ['manager', 'reportsto', 'linemanager'] },
      { key: 'notes', label: 'Notes', kind: 'text', aliases: ['comment', 'comments', 'remarks'] },
    ],
  },
];

/* ─────────────────────────── Auto-matching ─────────────────────────── */

/** 'Warranty ends' / 'warranty_ends' / 'WARRANTYENDS' all collapse to the same. */
function normalise(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** header → field key. Only confident matches; the user confirms the rest. */
export function autoMap(headers: string[], target: ImportTarget): Record<string, string> {
  const mapping: Record<string, string> = {};
  const taken = new Set<string>();

  const candidates = target.fields.map((f) => ({
    key: f.key,
    names: new Set([normalise(f.key), normalise(f.label), ...(f.aliases ?? []).map(normalise)]),
  }));

  for (const header of headers) {
    const n = normalise(header);
    if (!n) continue;
    const hit = candidates.find((c) => !taken.has(c.key) && c.names.has(n));
    if (hit) {
      mapping[header] = hit.key;
      taken.add(hit.key);
    }
  }
  return mapping;
}

/* ─────────────────────────── Coercion ─────────────────────────── */

/** Accepts ISO, UK d/m/y and US m/d/y, resolving the ambiguity toward UK.
    Returns '' when it cannot tell, rather than guessing a wrong date. */
export function parseDate(raw: string): { value: string; warning?: string } {
  const s = raw.trim();
  if (!s) return { value: '' };

  // ISO, already unambiguous.
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) {
    const [, y, m, d] = iso;
    return { value: `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` };
  }

  const parts = s.match(/^(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{2,4})$/);
  if (parts) {
    let [, a, b, y] = parts;
    let year = Number(y);
    if (year < 100) year += year < 70 ? 2000 : 1900;
    let day = Number(a);
    let month = Number(b);
    let warning: string | undefined;
    // UK order by default. If the first number cannot be a day, it must be US order.
    if (day > 12 && month <= 12) {
      // Unambiguously d/m/y.
    } else if (month > 12 && day <= 12) {
      [day, month] = [month, day];
      warning = `read "${s}" as month/day — US order`;
    } else if (day <= 12 && month <= 12 && day !== month) {
      warning = `"${s}" is ambiguous; read as day/month`;
    }
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return { value: '', warning: `"${s}" is not a valid date` };
    }
    const out = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return warning ? { value: out, warning } : { value: out };
  }

  // Last resort: let the platform try, but only trust a full parse.
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return { value: d.toISOString().slice(0, 10), warning: `interpreted "${s}" as a date` };
  }
  return { value: '', warning: `could not read "${s}" as a date` };
}

function coerce(
  field: ImportField,
  raw: string,
): { value: string | number | null; warning?: string } {
  const s = raw.trim();
  if (!s) return { value: null };

  switch (field.kind) {
    case 'date': {
      const { value, warning } = parseDate(s);
      return value ? { value, warning } : { value: null, warning };
    }
    case 'number': {
      const n = Number(s.replace(/[£$,\s]/g, ''));
      return Number.isFinite(n) ? { value: n } : { value: null, warning: `"${s}" is not a number` };
    }
    case 'depot':
    case 'choice': {
      const match = field.options?.find((o) => normalise(o) === normalise(s));
      if (match) return { value: match };
      return {
        value: null,
        warning: `"${s}" is not one of ${field.options?.join(', ')}`,
      };
    }
    default:
      return { value: s };
  }
}

/* The domain and the file now spell a depot the same way, so there is nothing
   to translate — a column holding "Hull" produces "Hull". Kept as a step rather
   than deleted because the value still has to be trimmed and stringified, and
   because this is where a future spelling correction would go. */

/* ─────────────────────────── Planning ─────────────────────────── */

export interface ExistingRecord {
  id: string;
  /** The value of the target's key field. */
  key: string;
  /** Current values, keyed by domain field name, for the change diff. */
  values: Record<string, unknown>;
}

export function buildPlan(
  table: CsvTable,
  mapping: Record<string, string>,
  target: ImportTarget,
  existing: ExistingRecord[],
): ImportPlan {
  const fieldByKey = new Map(target.fields.map((f) => [f.key, f]));
  const keyField = target.fields.find((f) => f.isKey);

  const mappedKeys = new Set(Object.values(mapping));
  const unmappedHeaders = table.headers.filter((h) => !mapping[h]);
  const missingRequired = target.fields.filter((f) => f.required && !mappedKeys.has(f.key));

  // Case-insensitive index, because asset tags get typed in lower case.
  const existingByKey = new Map(existing.map((e) => [e.key.toLowerCase(), e]));

  const rows: PlannedRow[] = [];
  const seenInFile = new Set<string>();
  const counts = { create: 0, update: 0, skip: 0, unchanged: 0 };

  table.rows.forEach((row, index) => {
    const line = index + 2; // +1 for zero-index, +1 for the header row
    const values: Record<string, string | number> = {};
    const warnings: string[] = [];

    for (const [header, fieldKey] of Object.entries(mapping)) {
      const field = fieldByKey.get(fieldKey);
      if (!field) continue;
      const { value, warning } = coerce(field, row[header] ?? '');
      if (warning) warnings.push(warning);
      if (value === null) continue;
      values[fieldKey] = field.kind === 'depot' ? String(value).trim() : value;
    }

    const keyValue = keyField ? String(values[keyField.key] ?? '') : '';

    const skip = (reason: string): void => {
      rows.push({ line, action: 'skip', keyValue, reason, values, changes: [], warnings });
      counts.skip += 1;
    };

    if (!keyField) return skip('no key field configured');
    if (!keyValue) return skip(`no ${keyField.label.toLowerCase()}`);

    const missing = target.fields.filter((f) => f.required && !values[f.key]);
    if (missing.length > 0) {
      return skip(`missing ${missing.map((f) => f.label.toLowerCase()).join(', ')}`);
    }

    // A file that lists the same key twice would otherwise create then update it
    // in one run, which reads as a successful import of bad data.
    const dupeKey = keyValue.toLowerCase();
    if (seenInFile.has(dupeKey)) {
      return skip(`${keyValue} appears more than once in this file`);
    }
    seenInFile.add(dupeKey);

    const match = existingByKey.get(dupeKey);
    if (!match) {
      rows.push({ line, action: 'create', keyValue, values, changes: [], warnings });
      counts.create += 1;
      return;
    }

    // Only mapped fields are compared, so an unmapped column cannot blank a value.
    const changes: FieldChange[] = [];
    for (const [fieldKey, next] of Object.entries(values)) {
      const current = match.values[fieldKey];
      const same =
        current === next ||
        (current == null && next === '') ||
        String(current ?? '') === String(next);
      if (!same) {
        changes.push({
          field: fieldKey,
          label: fieldByKey.get(fieldKey)?.label ?? fieldKey,
          from: current,
          to: next,
        });
      }
    }

    if (changes.length === 0) {
      rows.push({
        line,
        action: 'skip',
        keyValue,
        reason: 'already matches',
        values,
        changes: [],
        warnings,
        existingId: match.id,
      });
      counts.unchanged += 1;
      return;
    }

    rows.push({
      line,
      action: 'update',
      keyValue,
      values,
      changes,
      warnings,
      existingId: match.id,
    });
    counts.update += 1;
  });

  return {
    rows,
    counts,
    unmappedHeaders,
    missingRequired,
    ready: missingRequired.length === 0 && counts.create + counts.update > 0,
  };
}
